import React, { useState, useCallback } from "react";
import {
  Container,
  Paper,
  Box,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  LinearProgress,
} from "@mui/material";
import { createPackage } from "../../services/api";

export default function AddPackage() {
  const [noteText, setNoteText] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [snack, setSnack] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  // Handle image upload + preview
  const handleImageChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setPreview(URL.createObjectURL(file));
    }
  }, []);

  // Submit package
  const handleSubmit = useCallback(async () => {
    if (!noteText.trim()) {
      setSnack({
        open: true,
        severity: "error",
        message: "Please write package details first",
      });
      return;
    }

    setLoading(true);
    try {
      // Send data to backend
      await createPackage({
        noteText,
        imageUrl: preview || undefined,
      });

      setSnack({
        open: true,
        severity: "success",
        message: "Package added successfully!",
      });

      // Reset form
      setNoteText("");
      setImageFile(null);
      setPreview(null);
    } catch (err) {
      console.error("AddPackage error:", err);
      setSnack({
        open: true,
        severity: "error",
        message:
          err.response?.data?.error || err.message || "Failed to create package",
      });
    } finally {
      setLoading(false);
    }
  }, [noteText, preview]);

  return (
    <Box sx={{ pt: 10, pb: 6, px: 2 }}>
      <Container maxWidth="md">
        <Paper
          sx={{
            p: 4,
            boxShadow: "0 8px 30px rgba(2,6,23,0.5)",
          }}
        >
          <Typography variant="h6" gutterBottom>
            Add Package (Notepad Style)
          </Typography>
          <Typography variant="body2" sx={{ mb: 2 }} color="text.secondary">
            Just write your package details below — include everything like
            name, duration, profit, etc. The system will save the full text as-is.
          </Typography>

          <TextField
            label="Package Details"
            multiline
            minRows={10}
            fullWidth
            value={noteText}
            onChange={(e) => setNoteText(e.target.value)}
            placeholder={`Example:
Name: 3 Month Investment Plan
Duration: 3 Months
Investment Range: Minimum 20 USDT, Maximum 999 USDT.
Monthly Profit: 4%, with full deposit return after 3 months.
Profit Payment: Monthly, with no referral bonus.`}
          />

          <Box sx={{ mt: 3 }}>
            <Button variant="contained" component="label">
              {imageFile ? "Change Image" : "Upload Package Image"}
              <input
                type="file"
                accept="image/*"
                hidden
                onChange={handleImageChange}
              />
            </Button>

            {preview && (
              <Box sx={{ mt: 2 }}>
                <img
                  src={preview}
                  alt="Preview"
                  style={{
                    maxWidth: "100%",
                    borderRadius: 8,
                    boxShadow: "0 0 10px rgba(0,0,0,0.3)",
                  }}
                />
              </Box>
            )}
          </Box>

          {loading && <LinearProgress sx={{ mt: 3 }} />}

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              gap: 2,
              mt: 3,
            }}
          >
            <Button
              variant="outlined"
              onClick={() => {
                setNoteText("");
                setImageFile(null);
                setPreview(null);
              }}
            >
              Reset
            </Button>

            <Button
              variant="contained"
              color="primary"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? "Saving..." : "Add Package"}
            </Button>
          </Box>
        </Paper>

        <Snackbar
          open={snack.open}
          autoHideDuration={4000}
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
        >
          <Alert
            onClose={() => setSnack((s) => ({ ...s, open: false }))}
            severity={snack.severity}
            sx={{ width: "100%" }}
          >
            {snack.message}
          </Alert>
        </Snackbar>
      </Container>
    </Box>
  );
}
